import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Snowflake, Sun, Trash, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import MerchantLayout from "@/components/MerchantLayout";
import { StatusPill } from "@/components/Layout";
import { MERCHANT } from "@/lib/routes";
import { merchantCardsApi, errMsg } from "@/lib/merchantCards";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TABS = ["Overview", "Recharge", "Bills", "Physical", "Settings"];

const ISSUER_CARD_STATUS = {
  0: "Pending",
  1: "Inactive",
  2: "Closed",
  3: "Blocked",
  4: "Active",
  5: "Expired",
  6: "Terminated",
  7: "Frozen",
};

const ISSUER_CARD_TYPE = {
  1: "Physical",
  2: "Virtual",
};

function issuerLabel(map, value) {
  if (value == null || value === "") return "—";
  const key = Number(value);
  return map[key] ?? String(value);
}

function issuerPhone(detail) {
  const dial = detail?.dialCode ?? detail?.dial_code;
  const phone = detail?.phoneNumber ?? detail?.phone_number;
  if (!phone) return "—";
  return dial ? `+${dial} ${phone}` : String(phone);
}

function issuerBilling(detail) {
  const addr = detail?.billingAddress ?? detail?.billing_address;
  if (!addr || typeof addr !== "object") return "—";
  const line = addr.address || addr.line1 || "";
  const country = addr.countryArea || addr.country_area || addr.country || "";
  return [line, country].filter(Boolean).join(", ") || "—";
}

function issuerPan(detail) {
  const last4 = detail?.cardNumber ?? detail?.card_number;
  if (!last4) return "—";
  return `**** ${String(last4).slice(-4)}`;
}

function defaultBillRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} 00:00:00`;
  return {
    start: fmt(start),
    end: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")} 23:59:59`,
  };
}

export default function MerchantCardDetail() {
  const { cardId } = useParams();
  const [tab, setTab] = useState("Overview");
  const [card, setCard] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, d] = await Promise.all([
        merchantCardsApi.list(),
        merchantCardsApi.detail(cardId).catch(() => null),
      ]);
      const local = (listRes.data.items || []).find((c) => c.id === cardId) || null;
      setCard(local);
      setDetail(d?.data?.data ?? null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  useEffect(() => { load(); }, [load]);

  const action = async (fn, label) => {
    try {
      await fn();
      toast.success(label);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading && !card) {
    return (
      <MerchantLayout>
        <div className="p-10 text-sm text-zinc-500">Loading card…</div>
      </MerchantLayout>
    );
  }

  if (!card) {
    return (
      <MerchantLayout>
        <div className="p-10">
          <Link to={MERCHANT.cards} className="text-sm text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1">
            <ArrowLeft size={14} /> Back to cards
          </Link>
          <p className="mt-4 text-sm text-zinc-500">Card not found.</p>
        </div>
      </MerchantLayout>
    );
  }

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-card-detail-page">
        <Link to={MERCHANT.cards} className="text-sm text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Cards
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl tracking-tight font-medium flex items-center gap-2">
              <CreditCard size={28} />
              {card.masked_pan || card.id?.slice(0, 8) || cardId?.slice(0, 8) || "Card"}
            </h1>
            <p className="text-sm text-zinc-500 mt-1 capitalize">{card.type} · {card.currency} · ID <span className="font-mono text-xs">{card.id || cardId}</span></p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill value={card.status} />
            <span className="text-lg font-mono tabular-nums">{fmt$(card.balance)}</span>
            {card.status === "active" && (
              <button onClick={() => action(() => merchantCardsApi.freeze(cardId), "Card frozen")} className="btn-sm border">
                <Snowflake size={14} /> Freeze
              </button>
            )}
            {card.status === "frozen" && (
              <button onClick={() => action(() => merchantCardsApi.unfreeze(cardId), "Card unfrozen")} className="btn-sm border">
                <Sun size={14} /> Unfreeze
              </button>
            )}
            {card.status !== "terminated" && (
              <button
                onClick={() => {
                  if (!window.confirm("Terminate this card permanently?")) return;
                  action(() => merchantCardsApi.terminate(cardId, "portal"), "Card terminated");
                }}
                className="btn-sm border text-red-700 border-red-200 hover:bg-red-50"
              >
                <Trash size={14} /> Terminate
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-1 border-b border-zinc-200 mb-6">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && (
          <OverviewTab card={card} detail={detail} onRefresh={load} cardId={cardId} />
        )}
        {tab === "Recharge" && <RechargeTab cardId={cardId} onDone={load} />}
        {tab === "Bills" && <BillsTab cardId={cardId} />}
        {tab === "Physical" && <PhysicalTab cardId={cardId} card={card} onDone={load} />}
        {tab === "Settings" && <SettingsTab cardId={cardId} />}
      </div>
    </MerchantLayout>
  );
}

function OverviewTab({ card, detail, cardId, onRefresh }) {
  const [panUrl, setPanUrl] = useState(null);
  const [limits, setLimits] = useState(null);
  const [showRawIssuer, setShowRawIssuer] = useState(false);

  const loadSensitive = async () => {
    try {
      const [p, l] = await Promise.all([
        merchantCardsApi.panHtml(cardId),
        merchantCardsApi.limits(cardId),
      ]);
      setPanUrl(p.data?.data?.url || p.data?.data?.htmlUrl || JSON.stringify(p.data?.data));
      setLimits(l.data?.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Panel title="Local record">
        <KV k="Status" v={card.status} />
        <KV k="Type" v={card.type} />
        <KV k="Expiry" v={card.expiry || "—"} />
        <KV k="Balance" v={fmt$(card.balance)} />
        <KV k="Created" v={(card.created_at || "").slice(0, 10)} />
      </Panel>
      <Panel title="Issuer detail">
        {detail ? (
          <>
            <KV k="Issuer card ID" v={detail.cardId || detail.card_id || "—"} />
            <KV k="Product code" v={detail.cardCode || detail.card_code || "—"} />
            <KV k="Card number" v={issuerPan(detail)} />
            <KV k="Status" v={issuerLabel(ISSUER_CARD_STATUS, detail.cardStatus ?? detail.card_status)} />
            <KV k="Type" v={issuerLabel(ISSUER_CARD_TYPE, detail.cardType ?? detail.card_type)} />
            <KV k="Currency" v={detail.cardCurrency || detail.card_currency || "—"} />
            <KV k="Balance" v={fmt$(detail.cardBalance ?? detail.card_balance)} />
            <KV k="Frozen amount" v={fmt$(detail.cardFrozenAmount ?? detail.card_frozen_amount)} />
            <KV k="Email" v={detail.email || "—"} />
            <KV k="Phone" v={issuerPhone(detail)} />
            <KV k="Billing address" v={issuerBilling(detail)} />
            <button
              type="button"
              onClick={() => setShowRawIssuer((v) => !v)}
              className="mt-3 text-xs text-zinc-500 hover:text-zinc-800 underline-offset-2 hover:underline"
            >
              {showRawIssuer ? "Hide raw issuer response" : "Show raw issuer response"}
            </button>
            {showRawIssuer && (
              <pre className="mt-2 text-[11px] font-mono bg-zinc-100 text-zinc-800 p-3 rounded-sm overflow-auto max-h-48">
                {JSON.stringify(detail, null, 2)}
              </pre>
            )}
          </>
        ) : (
          <p className="text-sm text-zinc-500">No issuer detail available.</p>
        )}
        <button onClick={onRefresh} className="btn-sm border mt-3">Refresh</button>
      </Panel>
      <Panel title="Sensitive (issuer)" className="lg:col-span-2">
        <p className="text-xs text-zinc-500 mb-3">PAN HTML and limits are fetched on demand from the card issuer.</p>
        <button onClick={loadSensitive} className="btn-primary mb-3">Load PAN & limits</button>
        {panUrl && (
          <div className="mb-3">
            <div className="field">PAN HTML</div>
            {String(panUrl).startsWith("http") ? (
              <a href={panUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline break-all">{panUrl}</a>
            ) : (
              <pre className="text-xs font-mono bg-zinc-100 p-2 rounded-sm overflow-auto">{panUrl}</pre>
            )}
          </div>
        )}
        {limits && (
          <pre className="text-[11px] font-mono bg-zinc-100 p-3 rounded-sm overflow-auto">{JSON.stringify(limits, null, 2)}</pre>
        )}
      </Panel>
    </div>
  );
}

function RechargeTab({ cardId, onDone }) {
  const [amount, setAmount] = useState("50");
  const [feePreview, setFeePreview] = useState(null);

  const previewFee = async () => {
    try {
      const r = await merchantCardsApi.rechargeFee(cardId, Number(amount));
      setFeePreview(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submit = async () => {
    try {
      await merchantCardsApi.recharge(cardId, Number(amount));
      toast.success("Card recharged");
      setFeePreview(null);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Panel title="Load card">
      <label className="field">Amount (USD)</label>
      <input className="input mb-3" type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <div className="flex gap-2">
        <button onClick={previewFee} className="btn-sm border">Preview fee</button>
        <button onClick={submit} className="btn-primary">Recharge</button>
      </div>
      {feePreview && (
        <p className="mt-3 text-sm text-zinc-600">
          Fee {fmt$(feePreview.fee)} · Total debit from wallet {fmt$(feePreview.total)}
        </p>
      )}
      <p className="mt-3 text-xs text-zinc-500">Use the Bills tab for issuer transaction history.</p>
    </Panel>
  );
}

function BillsTab({ cardId }) {
  const range = defaultBillRange();
  const [start, setStart] = useState(range.start);
  const [end, setEnd] = useState(range.end);
  const [bills, setBills] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [billDetail, setBillDetail] = useState(null);

  const load = async () => {
    try {
      const r = await merchantCardsApi.bills({
        card_id: cardId,
        page: 1,
        limit: 20,
        create_time_start: start,
        create_time_end: end,
      });
      setBills(r.data?.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const loadDetail = async () => {
    if (!orderId) return;
    try {
      const r = await merchantCardsApi.billDetail(cardId, orderId);
      setBillDetail(r.data?.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4">
      <Panel title="Transaction history (issuer)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="field">Start (yyyy-MM-dd HH:mm:ss)</label>
            <input className="input" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="field">End</label>
            <input className="input" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <button onClick={load} className="btn-primary">Fetch bills</button>
        {bills && (
          <pre className="mt-4 text-[11px] font-mono bg-zinc-950 text-emerald-200 p-3 rounded-sm overflow-auto max-h-80">
            {JSON.stringify(bills, null, 2)}
          </pre>
        )}
      </Panel>
      <Panel title="Bill detail">
        <label className="field">Order ID</label>
        <div className="flex gap-2">
          <input className="input flex-1" value={orderId} onChange={(e) => setOrderId(e.target.value)} />
          <button onClick={loadDetail} className="btn-sm border">Load</button>
        </div>
        {billDetail && (
          <pre className="mt-3 text-[11px] font-mono bg-zinc-100 p-3 rounded-sm overflow-auto">{JSON.stringify(billDetail, null, 2)}</pre>
        )}
      </Panel>
    </div>
  );
}

function PhysicalTab({ cardId, card, onDone }) {
  const [activeCode, setActiveCode] = useState("");
  const [logistics, setLogistics] = useState(null);

  const activate = async () => {
    try {
      await merchantCardsApi.activate(cardId, activeCode);
      toast.success("Card activated");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const track = async () => {
    try {
      const r = await merchantCardsApi.order({ card_id: cardId });
      setLogistics(r.data?.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const isPhysical = (card.type || "").toLowerCase() === "physical";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Panel title="Activate physical card">
        {!isPhysical && <p className="text-sm text-zinc-500 mb-3">This card is not marked as physical.</p>}
        <label className="field">Activation code</label>
        <input className="input mb-3" value={activeCode} onChange={(e) => setActiveCode(e.target.value)} />
        <button onClick={activate} className="btn-primary">Activate</button>
      </Panel>
      <Panel title="Shipping / logistics">
        <button onClick={track} className="btn-primary mb-3">Track order</button>
        {logistics && (
          <pre className="text-[11px] font-mono bg-zinc-100 p-3 rounded-sm overflow-auto">{JSON.stringify(logistics, null, 2)}</pre>
        )}
      </Panel>
    </div>
  );
}

function SettingsTab({ cardId }) {
  const [email, setEmail] = useState("");
  const [dialCode, setDialCode] = useState("1");
  const [phone, setPhone] = useState("");
  const [authId, setAuthId] = useState("");
  const [fees, setFees] = useState(null);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Panel title="Contact">
        <label className="field">Email</label>
        <input className="input mb-2" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button onClick={() => merchantCardsApi.updateEmail(cardId, email).then(() => toast.success("Email updated")).catch((e) => toast.error(errMsg(e)))} className="btn-primary mb-4">
          Update email
        </button>
        <label className="field">Dial code</label>
        <input className="input mb-2" value={dialCode} onChange={(e) => setDialCode(e.target.value)} />
        <label className="field">Phone</label>
        <input className="input mb-2" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <button onClick={() => merchantCardsApi.updatePhone(cardId, Number(dialCode), phone).then(() => toast.success("Phone updated")).catch((e) => toast.error(errMsg(e)))} className="btn-primary">
          Update phone
        </button>
      </Panel>
      <Panel title="3DS & fees">
        <label className="field">3DS auth ID</label>
        <input className="input mb-2" value={authId} onChange={(e) => setAuthId(e.target.value)} />
        <div className="flex gap-2 mb-4">
          <button onClick={() => merchantCardsApi.respond3ds(authId, true).then(() => toast.success("Approved")).catch((e) => toast.error(errMsg(e)))} className="btn-sm border">Approve</button>
          <button onClick={() => merchantCardsApi.respond3ds(authId, false).then(() => toast.success("Rejected")).catch((e) => toast.error(errMsg(e)))} className="btn-sm border">Reject</button>
        </div>
        <button onClick={() => merchantCardsApi.queryFee(cardId).then((r) => setFees(r.data?.data)).catch((e) => toast.error(errMsg(e)))} className="btn-primary">
          Query card fees
        </button>
        {fees && <pre className="mt-3 text-[11px] font-mono bg-zinc-100 p-3 rounded-sm overflow-auto">{JSON.stringify(fees, null, 2)}</pre>}
      </Panel>
    </div>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`bg-white border border-zinc-200 rounded-sm p-5 ${className}`}>
      <h3 className="font-display text-lg tracking-tight font-medium mb-4">{title}</h3>
      {children}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between py-1.5 text-sm border-b border-zinc-50 last:border-0">
      <span className="text-zinc-500">{k}</span>
      <span className="font-medium capitalize">{String(v)}</span>
    </div>
  );
}
